/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2023 - 2023
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import SpinnerButton from "./SpinnerButton";
import {
  status as bidStatus,
  capacityMatchingStatus,
  mode
} from "../../../helpers/types";
import {
  fetchBidSummary,
  fetchBidMessages
} from "../../../slices/bidSummarySlice";
import {
  acceptCapacityMatch,
  fetchCapacityMatchLaneSummary,
  resetBannerActionState
} from "../../../slices/bidProcurementSlice";
import ErrorMessage from "./ErrorMessage";
import messages from "../../../helpers/messages";
import RejectBidModal from "../procurement/RejectBidModal";

function ProcurementViewPendingState({ role, summary }) {
  const [rejectBidModal, toggleRejectBidModal] = useState(false);
  const dispatch = useDispatch();
  const { acceptCapacityMatchIsLoading, acceptCapacityMatchFailure } =
    useSelector(state => state.bidProcurement);
  const route = useLocation().pathname.split("/")[3] || "";

  const { modes, bidId } = summary;
  const { bidModeId } = modes.find(
    x =>
      x.mode === mode.tl &&
      x.status === bidStatus.inProgress &&
      x.capacityMatchingStatus === capacityMatchingStatus.pending
  );

  const handleAcceptClick = useCallback(
    (bidModeId, bidId) => {
      dispatch(resetBannerActionState());
      dispatch(acceptCapacityMatch(bidModeId, bidId, role, route));
    },
    [dispatch, role, route]
  );

  const handleRejectModalClose = success => {
    toggleRejectBidModal(false);
    if (success) {
      dispatch(fetchBidSummary({ bidId, role }));
      route === "summary" && dispatch(fetchBidMessages(bidId));
      route === "procurement" && dispatch(fetchCapacityMatchLaneSummary(bidId));
    }
  };

  const openRejectModal = () => {
    dispatch(resetBannerActionState());
    toggleRejectBidModal(true);
  };

  return (
    <>
      <RejectBidModal
        open={rejectBidModal}
        bidId={bidId}
        bidModeId={bidModeId}
        handleClose={handleRejectModalClose}
      />
      <p className="inline-block font-size-14 pt-16 mb-0">
        {acceptCapacityMatchFailure ? (
          <ErrorMessage />
        ) : (
          messages.wouldYouLikeToRunMatchesBannerMessage.defaultMessage
        )}
      </p>
      <div className="fr py-8 my-2">
        <button
          className="text-button univers-67-bold-cond font-size-14 mx-8 h-32 text-red-failure-dark"
          data-testid="reject"
          onClick={openRejectModal}
          style={{ minWidth: "76px" }}
        >
          {messages.reject.defaultMessage}
        </button>
        {acceptCapacityMatchIsLoading ? (
          <SpinnerButton minWidth="126px" />
        ) : (
          <button
            className="text-button univers-67-bold-cond font-size-14 mx-8 h-32 bg-cobalt-blue text-white"
            data-testid="accept"
            onClick={() => handleAcceptClick(bidModeId, bidId)}
            style={{ minWidth: "126px" }}
          >
            {messages.runMatching.defaultMessage}
          </button>
        )}
      </div>
    </>
  );
}

export default ProcurementViewPendingState;
